import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { eventChannel } from "redux-saga";
import {
  Box,
  IconButton,
  InputBase,
  CircularProgress,
  Typography,
} from "@mui/material";
import { SendRounded, AttachFile, Mic, Mood } from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react"; // Assuming you're using this library
import {
  sendMessage,
  sendAttachment,
  startRecording,
  stopRecording,
  typingStatus,
  stopTypingStatus,
  editMessageSuccess,
  editMessage,
  editedMessageTextSuccess,
} from "../../../../Store/Buzzcom/actions"; // Adjust imports for your actions
import { debounce, throttle } from "lodash";
import { getSocket } from "../../../../services/socketService";
import apiServices from "../../../../services/apiServices";

const ChatInput = (props) => {
  const { updateSnackbar } = props;
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [inputMessage, setInputMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isEmojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messageType, setMessageType] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioStream, setAudioStream] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioRecorded, setAudioRecorded] = useState(false);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const isInCall = useSelector((state) => state.Buzzcom.isInCall);
  const messageEdit = useSelector((state) => state.Buzzcom.editMessage);
  const callUser = useSelector((state) => state.Buzzcom.callUser);
  const socket = getSocket();

  const hours = Math.floor(secondsElapsed / 3600);
  const minutes = Math.floor((secondsElapsed % 3600) / 60);
  const seconds = secondsElapsed % 60;
  // Format time for display
  const formattedTime = [hours, minutes, seconds]
    .map((v) => (v < 10 ? "0" + v : v))
    .join(":");
  // Redux States --------------------------------
  const selectedChat = useSelector((state) => state.Buzzcom.selectedChat);
  const sender = selectedChat?.contacts[0].userId;
  const receiver = selectedChat?.contacts[1].userId;
  const typingState = useSelector((state) => state.Buzzcom.isTyping);

  // const handleMicClick = () => {
  //   if (!isRecording) {
  //     dispatch(startRecording());
  //     setIsRecording(true);
  //   } else {
  //     dispatch(stopRecording());
  //     setIsRecording(false);
  //   }
  // };

  const handleMicClick = () => {
    console.log("CHAT_CALL_STATUS???", isInCall);
    if (isInCall) {
      updateSnackbar({
        severity: "error",
        message: "INACCESSIBLE IN CALL...",
        open: true,
      });
    } else {
      if (isRecording) {
        stopRecording();
        // setAudioRecorded(false);
        setAudioRecorded(true);
        setSecondsElapsed(0);
      } else {
        startRecording();
      }
    }
  };

  // const handleAttachClick = (event) => {
  //   const files = event.target.files;
  //   setAttachedFiles([...attachedFiles, ...Array.from(files)]);
  //   dispatch(sendAttachment(files));
  // };

  const handleAttachClick = async () => {
    // Create an input element to select files
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "audio/*, video/*, image/*"; // Allow audio, video, and image files
    fileInput.style.display = "none";

    // Trigger the file input when the attach button is clicked
    fileInput.addEventListener("change", async (e) => {
      setLoader(true);
      const selectedFiles = Array.from(e.target.files);

      // Create FormData and append selected files
      const formData = new FormData();
      selectedFiles.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });

      // Call the API service to upload attachments
      try {
        const response = await apiServices.uploadAttachments(formData);
        if (response?.imagePath) {
          setAttachment(true);
          setLoader(false);
          console.log(response, "attachments-----");
          setInputMessage(response?.imagePath);
          setMessageType("attachment");
        }
      } catch (error) {
        setLoader(false);
        console.error("Error uploading files:", error);
      }
    });

    fileInput.click();
  };

  const throttledTyping = throttle(() => {
    socket.emit("prv_typing_message", {
      typing: "typing",
      text: inputMessage,
      to: receiver,
      from: sender,
    });
  }, 1000);

  // Debounce "typing_end" event to send after 500ms of inactivity
  const debouncedTypingEnd = debounce(() => {
    socket.emit("prv_typing_message", {
      typing: "typing_end",
      text: inputMessage,
      to: receiver,
      from: sender,
    });
  }, 500);

  const handleSendAudio = async () => {
    // Stop the recording if it's in progress

    // Create FormData and append the audio blob

    const formData = new FormData();
    const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
    formData.append("audio", audioBlob, "recorded_audio.wav");

    try {
      // Call the API service to upload the audio file
      const response = await apiServices.uploadAttachments(formData);
      console.log(response.imagePath, "attachments");
      if (response?.imagePath) {
        // setAudioChunks([]);
        // setAudioBlob(null);
        setAttachment(true);
        setLoader(false);
        // setAudioRecorded(false);
        setInputMessage(response?.imagePath);
        setMessageType("attachment");
      }
    } catch (error) {
      setLoader(false);
      console.error("Error uploading files:", error);
    }

    // Notify the recipient that an audio message is included
    // setInputMessage("Audio message attached");

    // Optionally, you can send the audioBlob to a server or use it as needed.
  };

  const handleInputDelete = () => {
    setInputMessage("");
    setAttachment(false);
    setAudioChunks([]);
    setAudioBlob(null);
  };

  // const handleEmojiSelect = (emoji) => {
  //   setInputMessage(inputMessage + emoji.emoji);
  // };

  const handleEmojiSelect = (emojiObject) => {
    const emoji = emojiObject.emoji;
    setInputMessage((prevMessage) => prevMessage + emoji);
    setEmojiPickerVisible(false);

    // Focus the input field after adding the emoji
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // const handleSendClick = () => {
  //   if (inputMessage.trim() || attachedFiles.length > 0) {
  //     setLoader(true);
  //     dispatch(
  //       sendMessage({ message: inputMessage, attachments: attachedFiles })
  //     );
  //     setInputMessage("");
  //     setAttachedFiles([]);
  //     setLoader(false);
  //   }

  // };

  const callEditApi = async () => {
    const payload = {
      messageId: messageEdit?._id,
      text: inputMessage,
      to: selectedChat?.contacts[1].userId,
    };
    console.log("payload ##", payload);

    socket.emit("messageEdit", payload, (res) => {
      console.log("edit msg is!", res);

      if (res.status) {
        dispatch(editedMessageTextSuccess(res.message.text));
      }
    });
    let obj = {
      id: messageEdit?._id,
      updatedMessage: inputMessage,
    };
    await apiServices.updateMessage(obj);
    // setDeletedMessagId(selectedMessage?._id);
    // setIsEditing(false);

    setInputMessage("");
  };

  const handleSendClick = () => {
    if (isRecording) handleMicClick();
    if (messageEdit) {
      callEditApi(messageEdit);
      return;
    }
    setAttachment(false);
    if (inputMessage.trim() !== "") {
      // if (isEditing) {
      //   callEditApi();
      // } else {
      const conversationId = selectedChat?.conversationId;
      const messageWithEmoji = inputMessage + (selectedEmoji || "");
      let newMessage = {
        conversationId: conversationId,
        text: messageWithEmoji,
        messageType: messageType === "attachment" ? "attachment" : "text",
        to: receiver.toString(),
        from: sender.toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // socket.emit("ios_prv_message", newMessage);
      console.log("msg is", newMessage);
      socket.emit("ios_prv_message", newMessage, (response) => {
        console.log("Response from server emit:", response);
        if (response.status) {
          dispatch(sendMessage(newMessage));
        }
      });

      // if (contacts?.length > 2 && contacts[0]._id !== selectedChat._id) {
      //   setContacts(() => [
      //     ...contacts.filter((contact) => contact._id === selectedChat._id),
      //     ...contacts.filter((contact) => contact._id !== selectedChat._id),
      //   ]);
      // }
      console.log("Sending message: ", inputMessage);
      setInputMessage("");
      // }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  const handleKeyUp = () => {
    if (!isTyping) setIsTyping(true);
  };

  const renderMediaContentInput = (text) => {
    const isImageURL = /\.(gif|jpe?g|tiff?|png|webp|bmp)-\d+(\?.*)?$/i.test(
      text
    );
    const isVideoURL = /\.(mp4|webm|ogg)-\d+(\?.*)?$/i.test(text);
    const isAudioURL = /\.(mp3|wav)-\d+(\?.*)?$/i.test(text);
    const isFileURL = /\/[^/.]+\.\w+-\d+(\?.*)?$/i.test(text);

    const handleImageClick = () => {
      const anchor = document.createElement("a");
      anchor.href = text;
      anchor.download = text.split("/").pop();
      anchor.click();
    };

    const handleInputDelete = () => {
      setInputMessage("");
      setAttachment(null);
    };

    if (isImageURL) {
      return (
        <div className="d-flex align-items-center mb-3">
          <button
            className="btn btn-danger btn-sm me-2"
            onClick={() => handleInputDelete()}
          >
            ×
          </button>
          <img
            style={{ width: "120px", height: "100px", borderRadius: "10px" }}
            src={text}
            alt="Image"
          />
        </div>
      );
    } else if (isVideoURL) {
      return (
        <div className="d-flex align-items-center mb-3">
          <button
            className="btn btn-danger btn-sm me-2"
            onClick={() => handleInputDelete()}
          >
            ×
          </button>
          <video controls style={{ width: "120px" }}>
            <source src={text} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    } else if (isAudioURL) {
      return (
        <div className="d-flex align-items-center mb-3">
          <button
            className="btn btn-danger btn-sm me-2"
            onClick={() => handleInputDelete()}
          >
            ×
          </button>
          <audio controls src={text}>
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    } else if (isFileURL) {
      return (
        <div className="d-flex align-items-center mb-3">
          <button
            className="btn btn-danger btn-sm me-2"
            onClick={() => handleInputDelete()}
          >
            ×
          </button>
          <img
            style={{ width: "80px", height: "80px", borderRadius: "10px" }}
            onClick={handleImageClick}
            src={`${process.env.REACT_APP_PUBLIC_URL}/BuzzCom/images/filesample.jpg`}
            alt="File Thumbnail"
          />
        </div>
      );
    } else {
      // If none of the types match, return the text as is
      return text;
    }
  };

  let externalAudioChunks = [];
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        externalAudioChunks.push(e.data); // Use the renamed external variable
        setAudioChunks([...externalAudioChunks]);
      }
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(externalAudioChunks, { type: "audio/wav" });

      setAudioBlob(audioBlob); // Store the Blob in state
    };

    mediaRecorder.start();
    setIsRecording(true);
    setAudioStream(stream);
  };
  const stopRecording = () => {
    if (audioStream) {
      audioStream?.getTracks()?.forEach((track) => track.stop());
    }
    setIsRecording(false);
  };

  const setResetStates = () => {
    setInputMessage("");
    setAudioStream(null);
    setIsRecording(false);
    setAudioBlob(null);
  };

  // HOOKS ----------------------------------------------------------------

  useEffect(() => {
    if (selectedChat) {
      setResetStates();
    }

    return () => {
      setResetStates();
    };
  }, [selectedChat]);

  useEffect(() => {
    if (audioChunks && audioChunks?.length > 0 && audioRecorded) {
      handleSendAudio();
    }
  }, [audioChunks, audioRecorded]);

  // useEffect(() => {
  //   let interval;
  //   let isRunning = false;
  //   if (isRecording && !isRunning) {
  //     isRunning = true;
  //     interval = setInterval(() => {
  //       setSecondsElapsed((prevSeconds) => {
  //         return prevSeconds + 1;
  //       });
  //     }, 1000);
  //   }
  //   return () => {
  //     clearInterval(interval);
  //     isRunning = false;
  //   };
  // }, [isRecording]);

  useEffect(() => {
    let timer;

    if (isRecording) {
      // Start the timer when recording begins
      timer = setInterval(() => {
        setSecondsElapsed((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      // Clear the timer when recording stops
      clearInterval(timer);
    }

    // Clean up timer when the component is unmounted or recording stops
    return () => clearInterval(timer);
  }, [isRecording]);

  useEffect(() => {
    if (messageEdit) {
      setInputMessage(messageEdit.text);
    }
  }, [messageEdit]);

  useEffect(() => {
    if (isTyping) {
      dispatch(typingStatus());
    }
    const typingTimeout = setTimeout(() => {
      dispatch(stopTypingStatus());
      setIsTyping(false);
    }, 2000);

    return () => clearTimeout(typingTimeout);
  }, [isTyping, dispatch]);
  console.log(isInCall);
  return (
    selectedChat?.status !== "blocked" && (
      <div
        className="message-input"
        style={{ display: "flex", alignItems: "center" }}
      >
        {!attachment && (
          <>
            {!callUser && (
              <>
                {isRecording && (
                  <Typography variant="body1" className="ringingText px-2 pt-2">
                    {formattedTime}
                  </Typography>
                )}
                <IconButton
                  className="d-md-block d-none"
                  onClick={handleMicClick}
                  style={{
                    position: "absolute",
                    zIndex: 111,
                    marginLeft: "10px",
                  }}
                >
                  {isRecording ? (
                    <div className="micClr">
                      <Mic />{" "}
                    </div>
                  ) : (
                    <Mic />
                  )}
                </IconButton>
              </>
            )}
          </>
        )}

        {/* {isTyping ? (
        <p
          style={{
            fontSize: "11px",
            fontWeight: "700",
            textAlign: "center",
            position: "absolute",
            marginLeft: "50px",
            bottom: attachment ? "24%" : "13%",
          }}
        >
          Typing...
        </p>
      ) : null} */}

        {attachment ? (
          renderMediaContentInput(inputMessage)
        ) : (
          <>
            <div
              className="d-flex align-items-center msg-type-area"
              style={{
                background: "#F7F7F7",
                borderRadius: "10px",
                padding: "10px",
                margin: "15px",
                flex: 1,
              }}
            >
              <input
                ref={inputRef}
                type="text"
                className="form-control border-0"
                placeholder="Type a message"
                style={{
                  paddingLeft: "3%",
                  paddingRight: "11%",
                  background: "transparent",
                }}
                value={inputMessage}
                onKeyPress={handleKeyPress}
                onKeyUp={handleKeyUp}
                onChange={(e) => setInputMessage(e.target.value)}
              />
            </div>
          </>
        )}
        <div
          className="message-input-icons"
          style={{ position: "absolute", right: "115px" }}
        >
          {attachedFiles?.length > 0 && (
            <div>
              Attached Files:
              <ul>
                {attachedFiles?.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
          {!attachment && (
            <>
              <IconButton onClick={handleAttachClick}>
                <AttachFile />
              </IconButton>
              <IconButton
                onClick={() => {
                  setEmojiPickerVisible(!isEmojiPickerVisible);
                }}
              >
                <Mood />
              </IconButton>
            </>
          )}
          {isEmojiPickerVisible && (
            <EmojiPicker onEmojiClick={handleEmojiSelect} />
          )}
        </div>
        {loader ? (
          <Box sx={{ display: "flex" }}>
            <CircularProgress />
          </Box>
        ) : (
          <div className="colorIcon2">
            <IconButton onClick={handleSendClick}>
              <SendRounded
                style={{
                  backgroundColor: "#ff6500",
                  color: "white",
                  width: "50px",
                  height: "50px",
                  borderRadius: "10px",
                  fontSize: "10px",
                  padding: "6px",
                }}
              />
            </IconButton>
          </div>
        )}
      </div>
    )
  );
};

export default ChatInput;
